import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'


/** API */
import CareerApi from 'api/setting/default-users/career.api'

/** UTILS || SLICE */
import { resetModal, submitModal, unSubmitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import AddCircleIcon from '@mui/icons-material/AddCircle'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  CAREER_NAME: ``,
  IS_DUPLICATE_CAREER_NAME: ``,
  CAREER_NAME_STRING_BASE: ``,
  CAREER_NAME_STRING_EMPTY: ``,
  CAREER_NAME_MAXIMUM_LENGTH: ``,
  CAREER_NAME_ANY_REQUIRED: ``,

  CAREER_NAME_EN: ``,
  IS_DUPLICATE_CAREER_NAME_EN: ``,
  CAREER_NAME_EN_STRING_BASE: ``,
  CAREER_NAME_EN_STRING_EMPTY: ``,
  CAREER_NAME_EN_MAXIMUM_LENGTH: ``,
  CAREER_NAME_EN_ANY_REQUIRED: ``
}
export default function Careers() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [careers, setCareers] = useState([])

  const [careerId, setCareerId] = useState(Number)
  const [careerName, setCareerName] = useState('')
  const [careerNameEn, setCareerNameEn] = useState('')
  const [careerStatus, setCareerStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)

  const loadData = async () => {
    try {
      const resp = await CareerApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setCareers(resp.data)
      } else {
        setCareers([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const submit = () => {
    if (!careerName) return setErrorMessage({ ...errorMessage, ...{ CAREER_NAME: t('CAREER.MESSAGE.CAREER_NAME') } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    CareerApi.create(careerName, careerNameEn)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('CAREER.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CAREER.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CAREER.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {
    if (!careerName) return setErrorMessage({ ...errorMessage, ...{ CAREER_NAME: t(`CAREER.MESSAGE.CAREER_NAME`) } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    CareerApi.update(careerId, {
      careerName,
      careerNameEn,
      status: careerStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`CAREER.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CAREER.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CAREER.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setCareerId(0)
    setCareerName('')
    setCareerNameEn('')
    setCareerStatus('')
    setIsPopup(false)
    clearErrorMessage()
  }

  const onActive = () => {
    if (id) {
      CareerApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('CAREER.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('CAREER.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      CareerApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('CAREER.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('CAREER.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { careerId: crId, careerName: crName, careerNameEn: crNameEn, status: crStatus } = data
    setCareerId(crId)
    setCareerName(crName)
    setCareerNameEn(crNameEn)
    setCareerStatus(crStatus)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'careerName', disablePadding: false, label: t('CAREER.TABLE.CELL.CAREER_NAME') },
    { id: 'careerNameEn', disablePadding: false, label: t('CAREER.TABLE.CELL.CAREER_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('CAREER.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('CAREER.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { careerId: crId, careerName: crName, careerNameEn: crNameEn, updatedBy, updatedAt, status: crStatus } = objData
    const statusBtnActive = crStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = crStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: crId,
      id: crId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: crName },
        { option: 'TEXT', align: 'left', label: crNameEn },
        { option: 'STATUS', align: 'center', label: crStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.careerName)
    setNameEn(obj.careerNameEn)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`CAREER.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`CAREER.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { careerName: name, careerNameEn: nameEn && `(${nameEn})`, status: t(`CAREER.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('CAREER.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('CAREER.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />

              <MainButton
                title={t('CAREER.BUTTON.ADD')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => setIsPopup(true)}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={careers.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          title={careerId ? t('CAREER.TITLE_UPDATE') : t('CAREER.TITLE_CREATE')}
          isOpen={isPopup}
          onCancel={{ onClick: () => resetForm() }}
          onSubmit={{ onClick: () => careerId ? submitEdit() : submit(), disabled: disabledSubmit }}
          handleClose={resetForm}
          size={'XS'}
        >
          <Box py={2}>
            {careerId ? (
              <Box display="flex" justifyContent="space-between" alignItems="center" pb={1}>
                <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
                <SwitchButton
                  onChange={() => setCareerStatus(careerStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                  checked={careerStatus === 'ACTIVE' ? true : false}
                />
              </Box>
            ) : (
              ''
            )}

            <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
              <TextInput
                fullWidth
                onChange={(event) => {
                  setCareerName(event.target.value)
                  clearErrorMessage()
                }}
                value={careerName}
                label={t('CAREER.INPUT.CAREER_NAME')}
                helperText={
                  errorMessage.CAREER_NAME || errorMessage.IS_DUPLICATE_CAREER_NAME || errorMessage.CAREER_NAME_STRING_BASE || errorMessage.CAREER_NAME_STRING_EMPTY || errorMessage.CAREER_NAME_MAXIMUM_LENGTH || errorMessage.CAREER_NAME_ANY_REQUIRED
                }
                required={true}
              />
              <TextInput
                fullWidth
                onChange={(event) => {
                  setCareerNameEn(event.target.value)
                  clearErrorMessage()
                }}
                value={careerNameEn}
                helperText={
                  errorMessage.CAREER_NAME_EN ||
                  errorMessage.IS_DUPLICATE_CAREER_NAME_EN ||
                  errorMessage.CAREER_NAME_EN_STRING_BASE ||
                  errorMessage.CAREER_NAME_EN_STRING_EMPTY ||
                  errorMessage.CAREER_NAME_EN_MAXIMUM_LENGTH ||
                  errorMessage.CAREER_NAME_EN_ANY_REQUIRED
                }
                label={t('CAREER.INPUT.CAREER_NAME_EN')}
                required={false}
              />
            </Box>
          </Box>
        </PageModal>
      </Box>
    </Box>
  )
}
